import { useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RSVP } from '@/types';

/** "Standard x2, VIP x1" summary built directly from the RSVP's own ticket lines (no event lookup needed). */
function guestTicketSummary(g: RSVP): string {
  const lines = Array.isArray(g.responses?.Tickets) ? (g.responses.Tickets as Array<Record<string, unknown>>) : [];
  return lines
    .filter((line) => typeof line?.qty === 'number' && (line.qty as number) > 0)
    .map((line) => `${line.name as string} x${line.qty as number}`)
    .join(', ');
}

function guestDisplayName(g: RSVP): string {
  const combined = [g.firstName, g.lastName].filter(Boolean).join(' ').trim();
  if (combined) {
    return combined;
  }
  return g.name?.trim() || 'Guest';
}

function resolveInviterFromField(
  invitedBy: string | undefined,
  guests: RSVP[]
): RSVP | undefined {
  const t = invitedBy?.trim();
  if (!t) {
    return undefined;
  }
  return guests.find((g) => g.id === t || g.userId === t);
}

function inviteesOfHost(host: RSVP, guests: RSVP[]): RSVP[] {
  return guests.filter((g) => {
    if (g.id === host.id) {
      return false;
    }
    const ib = g.invitedBy?.trim();
    if (!ib) {
      return false;
    }
    return ib === host.id || (!!host.userId && ib === host.userId);
  });
}

/** Matches attendance list: prefix is often stored as rankShortName with rank empty. */
function rankAndUnitLine(g: RSVP): string | null {
  const mainPrefix = (g.rankShortName || g.rank || '').trim();
  const additional = (g.additionalRank || '').trim();
  const unit = (g.unit || '').trim();
  const segments = [mainPrefix, additional, unit].filter(Boolean);
  return segments.length > 0 ? segments.join(' · ') : null;
}

interface GuestAssignmentModalProps {
  tableId: string;
  tableName: string;
  seatNumber: number;
  currentGuest: RSVP | null;
  /** Full guest list for resolving invited-by relationships */
  allGuests?: RSVP[];
  availableGuests: RSVP[];
  onClose: () => void;
  onAssign: (tableId: string, seatNumber: number, guestId: string) => void;
  onUnassign: (tableId: string, seatNumber: number) => void;
}

export default function GuestAssignmentModal({
  tableId,
  tableName,
  seatNumber,
  currentGuest,
  allGuests = [],
  availableGuests,
  onClose,
  onAssign,
  onUnassign,
}: GuestAssignmentModalProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const relationshipLines = useMemo(() => {
    if (!currentGuest || allGuests.length === 0) {
      return [];
    }
    const lines: string[] = [];
    const inviter = resolveInviterFromField(currentGuest.invitedBy, allGuests);
    if (inviter) {
      lines.push(`Invited by: ${guestDisplayName(inviter)}`);
    }
    const invitees = inviteesOfHost(currentGuest, allGuests);
    if (invitees.length > 0) {
      lines.push(
        `Guests linked to this invitation: ${invitees.map(guestDisplayName).join(', ')}`
      );
    }
    return lines;
  }, [currentGuest, allGuests]);

  const currentGuestAffiliation = useMemo(
    () => (currentGuest ? rankAndUnitLine(currentGuest) : null),
    [currentGuest]
  );

  const filteredGuests = availableGuests.filter((guest) => {
    const q = searchQuery.toLowerCase();
    const hay = `${guest.name} ${guest.firstName ?? ''} ${guest.lastName ?? ''} ${guest.rank ?? ''} ${guest.rankShortName ?? ''} ${guest.additionalRank ?? ''} ${guest.unit ?? ''} ${guest.email ?? ''}`.toLowerCase();
    return hay.includes(q);
  });

  const handleAssign = (guestId: string) => {
    onAssign(tableId, seatNumber, guestId);
    onClose();
  };

  const handleUnassign = () => {
    onUnassign(tableId, seatNumber);
    onClose();
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-bold">Assign Guest</h2>
            <p className="text-sm text-muted-foreground">
              {tableName} - Seat {seatNumber}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          {/* Current Guest */}
          {currentGuest && (
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-blue-900">Currently Assigned</h3>
                <Button variant="destructive" size="sm" onClick={handleUnassign}>
                  Remove
                </Button>
              </div>
              <div className="text-sm">
                <p className="font-semibold">{guestDisplayName(currentGuest)}</p>
                {currentGuestAffiliation && (
                  <p className="text-blue-700">{currentGuestAffiliation}</p>
                )}
                {currentGuest.responses?.Meal && (
                  <p className="text-blue-600 mt-1">Meal: {currentGuest.responses.Meal}</p>
                )}
                {!currentGuest.responses?.Meal && guestTicketSummary(currentGuest) && (
                  <p className="text-blue-600 mt-1">Tickets: {guestTicketSummary(currentGuest)}</p>
                )}
                {relationshipLines.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-blue-200/70 pt-2 text-slate-700">
                    {relationshipLines.map((line, idx) => (
                      <p key={`rel-${idx}`}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search guests by name, rank, unit, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Available Guests */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            <h3 className="font-semibold text-sm text-muted-foreground">
              Available Guests ({filteredGuests.length})
            </h3>
            
            {filteredGuests.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <p>No guests available</p>
                {searchQuery && (
                  <p className="text-sm mt-2">Try adjusting your search</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredGuests.map((guest) => {
                  const affiliation = rankAndUnitLine(guest);
                  return (
                    <button
                      key={guest.id}
                      onClick={() => handleAssign(guest.id)}
                      className="w-full rounded-lg border bg-white p-3 text-left transition-all hover:border-primary hover:shadow-md"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{guestDisplayName(guest)}</p>
                          {affiliation && (
                            <p className="text-sm text-muted-foreground">{affiliation}</p>
                          )}
                          {guest.responses?.Meal && (
                            <p className="text-xs text-blue-600 mt-1">
                              Meal: {guest.responses.Meal}
                            </p>
                          )}
                          {!guest.responses?.Meal && guestTicketSummary(guest) && (
                            <p className="text-xs text-blue-600 mt-1">
                              Tickets: {guestTicketSummary(guest)}
                            </p>
                          )}
                        </div>
                        <span className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground shadow-sm">
                          Assign
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

